import { useState, useCallback, useRef } from "react";
import { Editor, type EditorOptions } from "@tiptap/react";

interface ManagedEditor {
  id: string;
  editor: Editor | null;
  isActive: boolean;
}

export const useEditorManager = () => {
  const [editors, setEditors] = useState<Map<string, ManagedEditor>>(new Map());
  const [activeEditorId, setActiveEditorId] = useState<string | null>(null);
  const [extensionsCache] = useState<Map<string, any>>(new Map());
  const editorsRef = useRef<Map<string, ManagedEditor>>(new Map());

  // Keep ref in sync with state
  editorsRef.current = editors;

  const registerEditor = useCallback(
    (id: string, editor: Editor, extensions?: any[]) => {
      setEditors((prevEditors) => {
        const newEditors = new Map(prevEditors);
        const existing = newEditors.get(id);

        // Only update if the editor instance has actually changed
        if (!existing || existing.editor !== editor) {
          newEditors.set(id, {
            id,
            editor,
            isActive: false,
          });
        }

        return newEditors;
      });

      // Cache extensions if provided
      if (extensions && extensions.length > 0) {
        extensionsCache.set(id, extensions);
      }
    },
    [extensionsCache]
  );

  const unregisterEditor = useCallback(
    (id: string) => {
      setEditors((prevEditors) => {
        const newEditors = new Map(prevEditors);
        const editor = newEditors.get(id);

        if (editor?.editor) {
          try {
            editor.editor.destroy();
          } catch (error) {
            console.warn("Error destroying editor:", error);
          }
        }

        newEditors.delete(id);
        return newEditors;
      });

      // Clear from cache
      extensionsCache.delete(id);

      // If this was the active editor, clear the active state
      if (activeEditorId === id) {
        setActiveEditorId(null);
      }
    },
    [activeEditorId, extensionsCache]
  );

  const setEditorInstance = useCallback((id: string, editor: Editor | null) => {
    setEditors((prevEditors) => {
      const existing = prevEditors.get(id);

      // Only update if the editor instance has actually changed
      // CRITICAL: Return prevEditors (same reference) if nothing changed to avoid re-renders
      if (!existing || existing.editor !== editor) {
        const newEditors = new Map(prevEditors);
        newEditors.set(id, {
          id,
          editor,
          isActive: existing?.isActive || false,
        });
        return newEditors;
      }

      // Return same reference to avoid triggering re-renders
      return prevEditors;
    });
  }, []);

  const getEditor = useCallback(
    (id: string): Editor | null => {
      return editorsRef.current.get(id)?.editor || null;
    },
    [] // No dependencies needed as we use ref
  );

  const getActiveEditor = useCallback((): Editor | null => {
    if (!activeEditorId) return null;
    return editorsRef.current.get(activeEditorId)?.editor || null;
  }, [activeEditorId]);

  const setActiveEditor = useCallback((id: string) => {
    setActiveEditorId(id);
    setEditors((prevEditors) => {
      const newEditors = new Map(prevEditors);

      // Update all editors' active state
      newEditors.forEach((editor, editorId) => {
        editor.isActive = editorId === id;
      });

      return newEditors;
    });
  }, []);

  const switchToEditor = useCallback(
    (id: string) => {
      // First disable all editing
      editorsRef.current.forEach((managed) => {
        if (managed.editor) {
          managed.editor.setEditable(false);
        }
      });

      // Then enable editing for the specific editor
      const targetEditor = editorsRef.current.get(id);
      if (targetEditor?.editor) {
        targetEditor.editor.setEditable(true);
        targetEditor.editor.commands.focus();
      }

      setActiveEditorId(id);
    },
    [] // No dependency on editors - access via ref
  );

  const focusEditor = useCallback(
    (id: string) => {
      const editor = editorsRef.current.get(id)?.editor;
      if (editor) {
        editor.commands.focus();
      }
    },
    [] // No dependency on editors - access via ref
  );

  const disableAllEditing = useCallback(() => {
    editorsRef.current.forEach((managed) => {
      if (managed.editor) {
        managed.editor.setEditable(false);
      }
    });
    setActiveEditorId(null);
  }, []); // No dependency on editors - access via ref

  const setEditorsEditable = useCallback(
    (editable: boolean) => {
      editorsRef.current.forEach((managed) => {
        if (managed.editor) {
          managed.editor.setEditable(editable);
        }
      });
      if (!editable) {
        setActiveEditorId(null);
      }
    },
    [] // No dependency on editors - access via ref
  );

  const enableEditingForEditor = useCallback(
    (id: string) => {
      // Disable all other editors first
      editorsRef.current.forEach((managed) => {
        if (managed.editor && managed.id !== id) {
          managed.editor.setEditable(false);
        }
      });

      // Enable the target editor
      const targetEditor = editorsRef.current.get(id);
      if (targetEditor?.editor) {
        targetEditor.editor.setEditable(true);
        targetEditor.editor.commands.focus();
      }

      setActiveEditorId(id);
    },
    [] // No dependency on editors - access via ref
  );

  const updateEditorContent = useCallback(
    (id: string, content: any, shouldEmitUpdate: boolean = false) => {
      const editor = editorsRef.current.get(id)?.editor;
      if (editor) {
        try {
          editor.commands.setContent(content, { emitUpdate: shouldEmitUpdate });
        } catch (error) {
          console.warn("Error updating editor content:", error);
        }
      }
    },
    [] // No dependency on editors - access via ref
  );

  const getExtensions = useCallback(
    (id: string) => {
      return extensionsCache.get(id) || [];
    },
    [extensionsCache]
  );

  return {
    // State
    editors,
    activeEditorId,

    // Core methods
    registerEditor,
    unregisterEditor,
    setEditorInstance,
    getEditor,
    getActiveEditor,
    setActiveEditor,

    // Editor control
    switchToEditor,
    focusEditor,
    disableAllEditing,
    setEditorsEditable,
    enableEditingForEditor,

    // Content management
    updateEditorContent,
    getExtensions,
  };
};
